import * as React from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';

function getSchemeForHost(): 'readcloud' | 'winc' {
  return window.location.host.startsWith('winc.') ? 'winc' : 'readcloud';
}

/**
 * Handles `/native/read/:cloudId/:bookId` when the URL wasn't intercepted by
 * the OS via Universal Links / App Links — i.e. the app isn't installed, OR
 * the platform doesn't support Universal Links (Windows, Linux, Chrome /
 * Firefox on macOS).
 *
 * Two side effects fire on mount:
 *   1. Attempt to launch the desktop Tauri app via the readcloud:// /
 *      winc:// custom scheme. The browser shows a protocol prompt the first
 *      time per origin; once the user accepts with "Always allow", future
 *      visits launch the app silently (mirrors the OAuth callback flow).
 *   2. Redirect to the in-app `/read/:cloudId/:bookId` route so the user
 *      sees the webapp reader behind the prompt. If they decline or the app
 *      isn't installed, they're already on the right page.
 *
 * On platforms that DO support Universal Links and the app IS installed,
 * this component never renders — the OS intercepts the URL before the page
 * loads.
 */
export function NativeUrlHandler() {
  const history = useHistory();
  const location = useLocation();
  const { cloudId = '', bookId = '' } = useParams<{
    cloudId: string;
    bookId: string;
  }>();

  React.useEffect(() => {
    if (!cloudId || !bookId) return;

    const scheme = getSchemeForHost();
    const customSchemeUrl = `${scheme}:///read/${encodeURIComponent(
      cloudId
    )}/${encodeURIComponent(bookId)}${location.search}`;
    const webappPath = `/read/${encodeURIComponent(cloudId)}/${encodeURIComponent(
      bookId
    )}${location.search}`;

    // Setting location.href to a custom scheme delegates to the OS without
    // navigating the page — so the SPA history.replace below proceeds and
    // the user lands on the webapp reader regardless of how they handle
    // the protocol prompt.
    window.location.href = customSchemeUrl;
    history.replace(webappPath);
  }, [cloudId, bookId, location.search, history]);

  if (!cloudId || !bookId) return <Redirect to="/" />;

  return null;
}
